import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef } from './data-table.ts';
import type { CassaCode, MotivoArchiviazione } from '@/types/entities/index.ts';
import { formatDate } from '../lib/utils.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import { getSindacatiMap } from '../lib/sindacati-info.ts';
import { getCasseMap } from '../lib/casse-info.ts';
import { raccogliDateFilters, handleDateOpChange, resetDateRanges } from '../lib/date-filter.ts';

// --- Configurazione colonne/azioni per stato ---

const STATO_ICON: Record<string, { badge: string; icon: string; label: string }> = {
  V: { badge: 'success', icon: 'bi-person-check', label: 'Attivo' },
  T: { badge: 'warning', icon: 'bi-arrow-left-right', label: 'Trasferito' },
  C: { badge: 'danger', icon: 'bi-person-x', label: 'Cessato' },
  D: { badge: 'dark', icon: 'bi-person-dash', label: 'Deceduto' },
};

function buildCassaHtml(row: Record<string, unknown>): string {
  const cassa = String(row.cassa ?? '');
  const cassaClass = getCasseMap()[cassa]?.badgeClass ?? 'secondary';
  const stato = String(row.statoLavoratore ?? '');
  const sindacato = String(row.sindacatoLavoratore ?? '');

  let html = '';
  const si = STATO_ICON[stato];
  if (si) {
    html += `<span class="text-${si.badge} mx-1" data-tooltip="${si.label}" data-position="top"><i class="bi ${si.icon}"></i></span>`;
  }
  // Lookup SSoT: id canonico Fenice 2/3/4 hanno siglaCategoriaEdile non vuota
  // (FILCA/FILLEA/FENEAL → icona dedicata). Id 1 (NESSUNO/Liberi) e id 5/6
  // (ALTRO/CGIL-UIL, siglaCategoriaEdile vuota) → nessuna icona.
  const info = sindacato ? getSindacatiMap()[sindacato] : undefined;
  const sigla = info?.siglaCategoriaEdile;
  if (sigla) {
    html += `<span class="sindacato ${sigla.toLowerCase()} mx-1" data-tooltip="${sigla}" data-position="top"></span>`;
  }
  html += `<span class="badge bg-${cassaClass}">${cassa}</span>`;
  if (row.daArchivio === 1) html += ' <span class="badge bg-warning small">Archivio</span>';
  if (stato === 'C') {
    const dataCess = row.dataCessazioneLavoratore ? ` <small>${formatDate(row.dataCessazioneLavoratore as string)}</small>` : '';
    html += ` <span class="badge bg-danger bg-opacity-75 small">Cessato${dataCess}</span>`;
  }
  return html;
}

const COLUMNS_BASE: ColumnDef[] = [
  { key: 'lavoratore.cognome', label: 'Lavoratore', type: 'link', href: '/lavoratori/{{lavoratoreId}}', text: '{{lavoratore.cognome}} {{lavoratore.nome}}', filterable: true, sortable: true },
  { key: 'lavoratore.codiceFiscale', label: 'Codice Fiscale', type: 'code', filterable: true, sortable: true },
  { key: 'dataNascitaLavoratore', label: 'Data nascita', type: 'date', emptyText: '-' },
  { key: 'telefonoLabel', label: 'Telefono', type: 'text', emptyText: '-' },
  { key: 'cassaHtml', label: 'Cassa', type: 'html' },
];

const DATE_COLUMN: Record<string, { key: string; label: string }> = {
  DA_PRESENTARE: { key: 'createdAt', label: 'Data ins.' },
  PRESENTATA: { key: 'dataPresentazione', label: 'Data presentaz.' },
};

const EMPTY_STATES: Record<string, { message: string; icon: string }> = {
  DA_PRESENTARE: { message: 'Nessuna delega da presentare', icon: 'file-earmark-plus' },
  PRESENTATA: { message: 'Nessuna delega in attesa di conferma', icon: 'send' },
};

function buildColumns(stato: string) {
  const cols: ColumnDef[] = [];
  if (stato === 'DA_PRESENTARE') {
    cols.push({ key: '_select', label: '<input type="checkbox" data-action="select-all">', type: 'html', cssClass: 'text-center' });
  }
  cols.push(...COLUMNS_BASE);
  if (stato === 'DA_PRESENTARE') {
    cols.push({ key: 'stato', label: 'Stato', type: 'badge', badgeFilter: 'stato_delega_badge', badgeTextFilter: 'stato_label', sortable: true });
  }
  const dateCol = DATE_COLUMN[stato] ?? { key: 'createdAt', label: 'Data' };
  cols.push({ key: dateCol.key, label: dateCol.label, type: 'date', sortable: true });
  cols.push({ key: 'note', label: 'Note', type: 'text', truncate: 30, emptyText: '-' });
  return cols;
}

function buildActions(stato: string) {
  if (stato === 'DA_PRESENTARE') {
    return [
      { name: 'dettagli', label: 'Dettagli', icon: 'info-circle' },
      { name: 'modifica', label: 'Modifica', icon: 'pencil-square' },
      { name: 'imposta-bozza', label: 'Metti come bozza', icon: 'pencil', visibleIf: { field: 'stato', value: 'DA_PRESENTARE' } },
      { name: 'imposta-da-presentare', label: 'Rimetti da presentare', icon: 'check2-square', visibleIf: { field: 'stato', value: 'BOZZA' } },
      { name: 'cancella', label: 'Cancella', icon: 'trash', btnClass: 'text-danger' },
    ];
  }
  return [
    { name: 'dettagli', label: 'Dettagli', icon: 'info-circle' },
    { name: 'archivia', label: 'Archivia', icon: 'archive', btnClass: 'text-warning' },
  ];
}

// --- Componente ---

export class ListaDeleghe extends AppComponent {
  static override componentName = 'wc-lista-deleghe';

  protected override get templateId() { return 'lista-deleghe'; }

  private stato = '';
  private dataTable: DataTable | null = null;
  private currentSort: string | null = null;
  private currentSortDir: 'asc' | 'desc' | null = null;
  private currentFilter = '';
  private currentPage = 1;
  private selectedIds = new Map<number, string>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private offcanvas: any = null;

  // Modali (spostate a body per z-index)
  private dettaglioModalEl: HTMLElement | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dettaglioModal: any = null;
  private presentaModalEl: HTMLElement | null = null;
  private archiviaModalEl: HTMLElement | null = null;
  private risultatoModalEl: HTMLElement | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private presentaModal: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private archiviaModal: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private risultatoModal: any = null;

  protected override buildRenderingData(): Record<string, unknown> {
    const stato = this.attributeString('stato');
    const empty = EMPTY_STATES[stato] ?? { message: 'Nessun elemento trovato', icon: 'inbox' };
    return {
      ...super.buildRenderingData(),
      stato,
      columnsJson: JSON.stringify(buildColumns(stato)),
      actionsJson: JSON.stringify(buildActions(stato)),
      emptyIcon: empty.icon,
      emptyMessage: empty.message,
      casse: Object.values(getCasseMap()),
    };
  }

  protected override afterRender(): void {
    this.stato = this.attributeString('stato');
    this.setupModals();

    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;

    this.setupTableEvents();
    this.setupFiltriRapidi();
    this.setupFiltriAvanzati();
    this.setupExportExcel();
    if (this.stato === 'DA_PRESENTARE') this.setupPresenta();
    if (this.stato === 'PRESENTATA') this.setupArchiviaModal();

    this.caricaDeleghe();
  }

  // --- Modali ---

  private setupModals(): void {
    this.dettaglioModalEl = this.querySelector('[data-modal="dettaglio-delega"]');
    if (this.dettaglioModalEl) {
      document.body.appendChild(this.dettaglioModalEl);
      this.dettaglioModal = new (globalThis as any).bootstrap.Modal(this.dettaglioModalEl);
    }
    this.presentaModalEl = this.querySelector('[data-modal="presenta"]');
    if (this.presentaModalEl) {
      document.body.appendChild(this.presentaModalEl);
      this.presentaModal = new (globalThis as any).bootstrap.Modal(this.presentaModalEl);
    }
    this.risultatoModalEl = this.querySelector('[data-modal="risultato-presenta"]');
    if (this.risultatoModalEl) {
      document.body.appendChild(this.risultatoModalEl);
      this.risultatoModal = new (globalThis as any).bootstrap.Modal(this.risultatoModalEl);
      const btnChiudi = this.risultatoModalEl.querySelector('[data-action="chiudi-risultato"]');
      if (btnChiudi) {
        this.listen(btnChiudi, 'click', () => {
          this.risultatoModal?.hide();
          this.currentPage = 1;
          this.caricaDeleghe();
        });
      }
    }
    this.archiviaModalEl = this.querySelector('[data-modal="archivia"]');
    if (this.archiviaModalEl) {
      document.body.appendChild(this.archiviaModalEl);
      this.archiviaModal = new (globalThis as any).bootstrap.Modal(this.archiviaModalEl);
    }
  }

  // --- Eventi data-table ---

  private setupTableEvents(): void {
    if (!this.dataTable) return;

    this.listen(this.dataTable, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page;
      this.caricaDeleghe();
    });

    this.listen(this.dataTable, 'dt-row-action', (e: Event) => {
      const { action, row } = (e as CustomEvent).detail;
      if (action === 'dettagli') this.showDettaglioDelega(row);
      if (action === 'modifica') this.handleModifica(row);
      if (action === 'cancella') this.handleCancella(row);
      if (action === 'archivia') this.openArchiviaModal(row);
      if (action === 'imposta-bozza') this.handleCambiaStato(row, 'BOZZA');
      if (action === 'imposta-da-presentare') this.handleCambiaStato(row, 'DA_PRESENTARE');
    });

    this.listen(this.dataTable, 'dt-render-complete', (e: Event) => {
      this.updatePresentaVisibility((e as CustomEvent).detail.count);
    });

    this.listen(this.dataTable, 'dt-sort-change', (e: Event) => {
      const { sort, dir } = (e as CustomEvent).detail;
      this.currentSort = sort;
      this.currentSortDir = dir;
      this.currentPage = 1;
      this.caricaDeleghe();
    });

    this.listen(this.dataTable, 'dt-filter-change', (e: Event) => {
      this.currentFilter = (e as CustomEvent).detail.filter;
      this.currentPage = 1;
      this.caricaDeleghe();
    });

    if (this.stato === 'DA_PRESENTARE') {
      this.listen(this.dataTable, 'change', (e: Event) => {
        const target = e.target as HTMLInputElement;
        if (target.matches('[data-check-id]')) {
          const id = Number(target.dataset.checkId);
          if (target.checked) this.selectedIds.set(id, target.dataset.checkCassa!); else this.selectedIds.delete(id);
          this.updateSelectAllCheckbox();
          this.updatePresentaButton();
        }
        if (target.matches('[data-action="select-all"]')) {
          const checked = target.checked;
          this.dataTable?.querySelectorAll('[data-check-id]').forEach(cb => {
            const input = cb as HTMLInputElement;
            const id = Number(input.dataset.checkId);
            input.checked = checked;
            if (checked) this.selectedIds.set(id, input.dataset.checkCassa!); else this.selectedIds.delete(id);
          });
          this.updatePresentaButton();
        }
      });
    }
  }

  // --- Filtri rapidi (button toggle) ---

  private setupFiltriRapidi(): void {
    this.delegate.on('click', '[data-filtro-rapido] button', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('button') as HTMLElement;
      const group = btn.closest('[data-filtro-rapido]') as HTMLElement;
      const campo = group.dataset.filtroRapido!;
      const value = btn.dataset.value!;
      const wasActive = btn.classList.contains('active');

      group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      if (!wasActive) btn.classList.add('active');

      const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
      if (select) select.value = wasActive ? '' : value;

      this.currentPage = 1;
      this.caricaDeleghe();
    });
  }

  // --- Filtri avanzati (offcanvas) ---

  private setupFiltriAvanzati(): void {
    const offcanvasEl = this.$('[data-offcanvas="filtri"]');
    if (offcanvasEl) {
      this.offcanvas = new (globalThis as any).bootstrap.Offcanvas(offcanvasEl);
    }

    this.delegate.on('click', '[data-action="apri-filtri"]', () => {
      this.offcanvas?.show();
    });

    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.offcanvas?.hide();
        this.syncButtonGroup('cassa');
        this.currentPage = 1;
        this.caricaDeleghe();
      });
    }

    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      this.resetFiltri();
      this.offcanvas?.hide();
      this.currentPage = 1;
      this.caricaDeleghe();
    });

    this.delegate.on('change', '[data-date-op]', (e: Event) => handleDateOpChange(this, e));
  }

  private setupExportExcel(): void {
    this.delegate.on('click', '[data-action="export-excel"]', (e: Event) => {
      e.preventDefault();
      const filtri = this.raccogliFiltri();
      const params = new URLSearchParams();
      const stati = this.stato === 'DA_PRESENTARE' ? 'BOZZA,DA_PRESENTARE' : this.stato;
      params.set('stato', stati);
      for (const [k, v] of Object.entries(filtri)) {
        if (v) params.set(k, v);
      }
      if (this.currentFilter) params.set('q', this.currentFilter);
      window.open(`/api/deleghe/export-csv?${params.toString()}`, '_blank');
    });
  }

  private syncButtonGroup(campo: string): void {
    const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
    const group = this.$(`[data-filtro-rapido="${campo}"]`);
    if (!select || !group) return;
    const val = select.value;
    group.querySelectorAll('button').forEach(b => {
      const btn = b as HTMLElement;
      btn.classList.toggle('active', btn.dataset.value === val);
    });
  }

  private raccogliFiltri(): Record<string, string> {
    const params: Record<string, string> = {};
    const cassa = (this.$('[data-filtro-val="cassa"]') as HTMLSelectElement | null)?.value;
    if (cassa) params.cassa = cassa;
    const da = (this.$('[data-filtro-val="da"]') as HTMLInputElement | null)?.value;
    const a = (this.$('[data-filtro-val="a"]') as HTMLInputElement | null)?.value;
    if (da) params.da = da;
    if (a) params.a = a;
    const statoLavoratore = (this.$('[data-filtro-val="statoLavoratore"]') as HTMLSelectElement | null)?.value;
    if (statoLavoratore) params.statoLavoratore = statoLavoratore;
    Object.assign(params, raccogliDateFilters(this, ['dataNascita']));
    const note = (this.$('[data-filtro-val="note"]') as HTMLInputElement | null)?.value;
    if (note) params.note = note;
    return params;
  }

  private resetFiltri(): void {
    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    form?.reset();
    resetDateRanges(this);
    this.syncButtonGroup('cassa');
  }

  private updateFiltriCount(): void {
    const filtri = this.raccogliFiltri();
    const count = Object.keys(filtri).length;
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('d-none', count === 0);
    }
    const resetBtn = this.$('[data-action="reset-filtri-toolbar"]');
    resetBtn?.classList.toggle('d-none', count === 0);
  }

  // --- Caricamento dati ---

  private async caricaDeleghe(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();

    const filtri = this.raccogliFiltri();
    this.updateFiltriCount();

    const opts = {
      ...filtri,
      page: this.currentPage,
      limit: 30,
      sort: this.currentSort ?? undefined,
      sortDir: this.currentSortDir ?? undefined,
      q: this.currentFilter || undefined,
    };

    const result = this.stato === 'PRESENTATA'
      ? await this.api.deleghe.presentate(opts)
      : await this.api.deleghe.daPresentare(opts);

    if (!result.ok) {
      this.dataTable.setError(result.error);
      this.updatePresentaVisibility(0);
      return;
    }

    const { deleghe, count, totalPages, pageSize, page } = result.data;
    const rows = deleghe.map(d => {
      const raw = d as Record<string, unknown>;
      const lav = raw.lavoratore as Record<string, unknown> | null;
      const row: Record<string, unknown> = {
        ...raw,
        _rowClass: raw.stato === 'BOZZA' ? 'table-warning' : '',
        cassaHtml: buildCassaHtml(raw),
        telefonoLabel: lav ? (lav.cellulare || lav.telefono || null) : null,
      };
      if (this.stato === 'DA_PRESENTARE') {
        const checked = this.selectedIds.has(raw.id as number) ? ' checked' : '';
        row._select = `<input type="checkbox" data-check-id="${raw.id}" data-check-cassa="${raw.cassa}"${checked}>`;
      }
      return row;
    });
    this.dataTable.setData(rows, { page, totalPages, count, pageSize });
    this.updatePresentaButton();
  }

  // --- Azioni riga ---

  private async handleModifica(row: Record<string, unknown>): Promise<void> {
    const id = row.id as number;
    const result = await Weext.modalIframe(`/deleghe/${id}/modifica?layout=modal`, {
      title: 'Modifica delega',
      size: 'lg',
    });
    if (result.refresh) {
      this.caricaDeleghe();
    }
  }

  private async handleCancella(row: Record<string, unknown>): Promise<void> {
    const lav = row.lavoratore as Record<string, unknown> | undefined;
    const nome = `${lav?.cognome ?? ''} ${lav?.nome ?? ''}`.trim();
    const ok = await Weext.confirm(`Cancellare la delega di ${nome}?`, {
      title: 'Conferma cancellazione',
      confirmText: 'Cancella',
      confirmClass: 'btn-danger',
    });
    if (!ok) return;

    const result = await this.api.deleghe.elimina(row.id as number);
    if (result.ok) {
      Weext.toast('Delega cancellata', { type: 'success' });
      this.caricaDeleghe();
    } else {
      Weext.toast(result.error, { type: 'danger' });
    }
  }

  private async handleCambiaStato(row: Record<string, unknown>, nuovoStato: 'BOZZA' | 'DA_PRESENTARE'): Promise<void> {
    const result = await this.api.deleghe.cambiaStato(row.id as number, nuovoStato);
    if (result.ok) {
      const msg = nuovoStato === 'BOZZA' ? 'Delega impostata come bozza' : 'Delega pronta per la presentazione';
      Weext.toast(msg, { type: 'success' });
      this.caricaDeleghe();
    } else {
      Weext.toast(result.error, { type: 'danger' });
    }
  }

  private openArchiviaModal(row: Record<string, unknown>): void {
    if (!this.archiviaModalEl) return;
    (this.archiviaModalEl.querySelector('[data-archivia-id]') as HTMLInputElement).value = String(row.id ?? '');
    (this.archiviaModalEl.querySelector('[data-archivia-motivo]') as HTMLSelectElement).value = '';
    const noteContainer = this.archiviaModalEl.querySelector('[data-note-container]') as HTMLElement;
    noteContainer?.classList.add('d-none');
    (this.archiviaModalEl.querySelector('[data-archivia-note]') as HTMLTextAreaElement).value = '';
    this.updateNoteCounter();
    this.archiviaModal?.show();
  }

  // --- Presenta tutte ---

  private setupPresenta(): void {
    if (!this.presentaModalEl) return;
    this.renderPresentaRadioCasse();

    this.delegate.on('click', '[data-action="presenta"]', () => {
      if (!this.presentaModalEl) return;
      const cassaContainer = this.presentaModalEl.querySelector('[data-presenta-cassa-container]') as HTMLElement | null;
      const msgEl = this.presentaModalEl.querySelector('[data-presenta-msg]') as HTMLElement | null;
      const titleEl = this.presentaModalEl.querySelector('[data-presenta-title]') as HTMLElement | null;

      const cassaFiltro = this.raccogliFiltri().cassa ?? '';
      const nSel = this.selectedIds.size;
      if (nSel > 0) {
        // Blocca subito se casse miste
        const casse = new Set(this.selectedIds.values());
        if (casse.size > 1) {
          Weext.toast('Le deleghe selezionate appartengono a casse diverse. Filtra per cassa o correggi la selezione.', { type: 'warning' });
          return;
        }
        const cassaSel = casse.values().next().value as string;
        cassaContainer?.classList.add('d-none');
        if (msgEl) {
          msgEl.textContent = `Verranno presentate ${nSel} deleghe selezionate ${cassaSel}.`;
          msgEl.classList.remove('d-none');
        }
        if (titleEl) titleEl.textContent = `Presenta ${nSel} deleghe ${cassaSel}`;
      } else if (cassaFiltro) {
        cassaContainer?.classList.add('d-none');
        if (msgEl) {
          msgEl.textContent = `Verranno presentate tutte le deleghe ${cassaFiltro} pronte.`;
          msgEl.classList.remove('d-none');
        }
        if (titleEl) titleEl.textContent = `Presenta deleghe ${cassaFiltro}`;
      } else {
        const casse = Object.values(getCasseMap());
        if (casse.length === 1) {
          cassaContainer?.classList.add('d-none');
        } else {
          cassaContainer?.classList.remove('d-none');
        }
        if (msgEl) msgEl.classList.add('d-none');
        if (titleEl) titleEl.textContent = 'Presenta deleghe';
      }

      const casse = Object.values(getCasseMap());
      const defaultCassa = cassaFiltro || casse[0]?.codice || '';
      const checked = this.presentaModalEl.querySelector(
        `[data-presenta-cassa] input[value="${defaultCassa}"]`
      ) as HTMLInputElement | null;
      if (checked) checked.checked = true;
      // Popola select mese/anno con valori correnti
      const now = new Date();
      const meseSelect = this.presentaModalEl.querySelector('[data-presenta-mese]') as HTMLSelectElement | null;
      const annoSelect = this.presentaModalEl.querySelector('[data-presenta-anno]') as HTMLSelectElement | null;
      if (meseSelect) meseSelect.value = String(now.getMonth() + 1).padStart(2, '0');
      if (annoSelect) {
        const annoCorrente = now.getFullYear();
        annoSelect.innerHTML = '';
        for (let a = annoCorrente - 1; a <= annoCorrente + 1; a++) {
          const opt = document.createElement('option');
          opt.value = String(a);
          opt.textContent = String(a);
          if (a === annoCorrente) opt.selected = true;
          annoSelect.appendChild(opt);
        }
      }
      this.presentaModal?.show();
    });

    const form = this.presentaModalEl.querySelector('[data-form="presenta"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', async (e: Event) => {
        e.preventDefault();
        if (!this.presentaModalEl) return;

        let cassa: CassaCode;
        let ids: number[] | undefined;

        if (this.selectedIds.size > 0) {
          // Cassa già validata all'apertura modale (casse miste bloccate)
          cassa = new Set(this.selectedIds.values()).values().next().value as CassaCode;
          ids = Array.from(this.selectedIds.keys());
        } else {
          // Presenta tutte: cassa da filtro o radio
          const cassaFiltro = this.raccogliFiltri().cassa ?? '';
          if (cassaFiltro) {
            cassa = cassaFiltro as CassaCode;
          } else {
            const cassaRadio = this.presentaModalEl.querySelector(
              '[data-presenta-cassa] input[name="presenta-cassa"]:checked'
            ) as HTMLInputElement | null;
            if (!cassaRadio?.value) return;
            cassa = cassaRadio.value as CassaCode;
          }
        }

        const mese = (this.presentaModalEl.querySelector('[data-presenta-mese]') as HTMLSelectElement | null)?.value;
        const anno = (this.presentaModalEl.querySelector('[data-presenta-anno]') as HTMLSelectElement | null)?.value;
        if (!mese || !anno) return;
        const data = `${anno}-${mese}-01`;

        const btnConferma = form.querySelector('[type="submit"]') as HTMLButtonElement;
        btnConferma.disabled = true;
        btnConferma.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Presentazione...';
        const result = await this.api.deleghe.presenta(cassa, data, ids);
        if (result.ok) {
          this.presentaModal?.hide();
          this.selectedIds.clear();
          this.updatePresentaButton();
          btnConferma.disabled = false;
          btnConferma.innerHTML = 'Conferma';
          this.showRisultatoPresenta(result.data.count, cassa, data, result.data.codaId, result.data.accorpata);
        } else {
          Weext.toast(result.error, { type: 'danger' });
          btnConferma.disabled = false;
          btnConferma.innerHTML = 'Conferma';
        }
      });
    }
  }

  private renderPresentaRadioCasse(): void {
    if (!this.presentaModalEl) return;
    const container = this.presentaModalEl.querySelector('[data-presenta-cassa]') as HTMLElement | null;
    if (!container) return;
    const casse = Object.values(getCasseMap());
    container.innerHTML = casse.map((c, i) => `
      <div class="form-check">
        <input class="form-check-input" type="radio" name="presenta-cassa" value="${c.codice}" id="presenta-cassa-${c.codice}"${i === 0 ? ' checked' : ''}>
        <label class="form-check-label" for="presenta-cassa-${c.codice}">${c.codice} - ${c.nome}</label>
      </div>
    `).join('');
  }

  private showRisultatoPresenta(count: number, cassa: string, data: string, codaId: number, accorpata: boolean): void {
    if (!this.risultatoModalEl) return;
    const dataFormatted = formatDate(data);
    const msgEl = this.risultatoModalEl.querySelector('[data-risultato-msg]');
    if (msgEl) {
      const azione = accorpata ? 'aggiunte alla presentazione' : 'presentate';
      msgEl.textContent = `${count} delegh${count === 1 ? 'a' : 'e'} ${azione} per ${cassa}.`;
    }
    const csvLink = this.risultatoModalEl.querySelector('[data-risultato-csv-link]') as HTMLAnchorElement | null;
    if (csvLink) csvLink.href = `/api/deleghe/csv/${codaId}`;
    this.risultatoModal?.show();
  }

  // --- Archivia (setup modale) ---

  private setupArchiviaModal(): void {
    if (!this.archiviaModalEl) return;
    const modalEl = this.archiviaModalEl;

    const motivoSelect = modalEl.querySelector('[data-archivia-motivo]') as HTMLSelectElement | null;
    if (motivoSelect) {
      this.listen(motivoSelect, 'change', () => {
        const noteContainer = modalEl.querySelector('[data-note-container]') as HTMLElement;
        if (motivoSelect.value === 'ALTRO') noteContainer?.classList.remove('d-none');
        else noteContainer?.classList.add('d-none');
      });
    }

    const noteTextarea = modalEl.querySelector('[data-archivia-note]') as HTMLTextAreaElement | null;
    if (noteTextarea) {
      this.listen(noteTextarea, 'input', () => this.updateNoteCounter());
    }

    const form = modalEl.querySelector('[data-form="archivia"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', async (e: Event) => {
        e.preventDefault();
        const id = Number((modalEl.querySelector('[data-archivia-id]') as HTMLInputElement)?.value);
        const motivo = (modalEl.querySelector('[data-archivia-motivo]') as HTMLSelectElement)?.value as MotivoArchiviazione;
        if (!motivo) {
          Weext.toast('Seleziona un motivo', { type: 'warning' });
          return;
        }

        let note: string | undefined;
        if (motivo === 'ALTRO') {
          note = (modalEl.querySelector('[data-archivia-note]') as HTMLTextAreaElement)?.value;
          if (!note || note.length < 100) {
            Weext.toast('Le note devono essere di almeno 100 caratteri', { type: 'warning' });
            return;
          }
        }

        const btnConferma = form.querySelector('[type="submit"]') as HTMLButtonElement;
        btnConferma.disabled = true;
        btnConferma.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Archiviazione...';

        const result = await this.api.deleghe.archivia(id, motivo, note);
        if (result.ok) {
          Weext.toast('Delega archiviata', { type: 'success' });
          this.archiviaModal?.hide();
          this.caricaDeleghe();
        } else {
          Weext.toast(result.error, { type: 'danger' });
        }

        btnConferma.disabled = false;
        btnConferma.innerHTML = '<i class="bi bi-archive me-1"></i>Archivia';
      });
    }
  }

  // --- Dettaglio delega ---

  private showDettaglioDelega(row: Record<string, unknown>): void {
    if (!this.dettaglioModalEl) return;
    const content = this.dettaglioModalEl.querySelector('[data-delega-content]') as HTMLElement;
    if (!content) return;

    const lav = row.lavoratore as Record<string, unknown> | undefined;
    templateParseToElement('shared-dettaglio-delega', content, {
      ...row,
      lavoratoreId: row.lavoratoreId,
      lavoratoreNome: lav ? `${lav.cognome} ${lav.nome}` : null,
      lavoratoreCf: lav?.codiceFiscale ?? null,
      operatoreNome: row.operatoreNome ?? null,
    });
    this.dettaglioModal?.show();
  }

  // --- Helper ---

  private updatePresentaButton(): void {
    const btn = this.$('[data-action="presenta"]');
    if (!btn) return;
    const n = this.selectedIds.size;
    if (n > 0) {
      btn.innerHTML = `<i class="bi bi-send me-1"></i>Presenta ${n} selezionat${n === 1 ? 'a' : 'e'}`;
    } else {
      btn.innerHTML = '<i class="bi bi-send me-1"></i>Presenta intera Cassa';
    }
  }

  private updateSelectAllCheckbox(): void {
    if (!this.dataTable) return;
    const selectAll = this.dataTable.querySelector('[data-action="select-all"]') as HTMLInputElement | null;
    if (!selectAll) return;
    const checkboxes = this.dataTable.querySelectorAll('[data-check-id]');
    const allChecked = checkboxes.length > 0 && Array.from(checkboxes).every(cb => (cb as HTMLInputElement).checked);
    selectAll.checked = allChecked;
  }

  private updatePresentaVisibility(count: number): void {
    const btn = this.$('[data-action="presenta"]');
    if (!btn) return;
    btn.classList.toggle('d-none', count === 0);
  }

  private updateNoteCounter(): void {
    if (!this.archiviaModalEl) return;
    const textarea = this.archiviaModalEl.querySelector('[data-archivia-note]') as HTMLTextAreaElement | null;
    const counter = this.archiviaModalEl.querySelector('[data-note-counter]');
    if (textarea && counter) {
      const len = textarea.value.length;
      counter.textContent = `${len} / 100 caratteri`;
      counter.classList.toggle('text-danger', len > 0 && len < 100);
    }
  }

  // --- Cleanup ---

  protected override onDisconnected(): void {
    this.offcanvas?.dispose();
    this.dettaglioModal?.dispose();
    this.presentaModal?.dispose();
    this.risultatoModal?.dispose();
    this.archiviaModal?.dispose();
    this.dettaglioModalEl?.remove();
    this.presentaModalEl?.remove();
    this.risultatoModalEl?.remove();
    this.archiviaModalEl?.remove();
  }
}
